import type { FC } from 'react';
import React, { useRef, useState } from 'react';
import cn from 'classnames';

import { TimerTextDisplay } from './components/timer-text-display';
import type { TTimerText, TValues } from './models';
import { VALUES } from './config';

export const TimerText: FC<TTimerText> = ({
    Case,
    CountdownTimer,
    Switch,
    Text,
    Timer,
    breakPoint = '14:00:00',
    styles,
    timerCount,
    timerTemplate = '#TIMER#',
    timerText,
    todayText = ' сегодня при оформлении заявки в течение #TIMER#.',
    todayTextTimerExpired,
    tomorrowText = ' завтра при оформлении заявки сегодня.',
    tomorrowTextTimerActive,
}) => {
    const isBreakPointTimerActive = useRef<boolean>();
    const isAdditionalSettings = Boolean(
        todayTextTimerExpired && tomorrowTextTimerActive && timerCount
    );
    const [visible, setVisible] = useState<TValues>(VALUES.TODAY);
    const isToday = visible === VALUES.TODAY;
    const [startTodayText, endTodayText] = todayText.split(timerTemplate);
    const [startTomorrowActiveText, endTomorrowActiveText] =
        tomorrowTextTimerActive?.split(timerTemplate) || [];

    const onTimerStart = () => {
        isBreakPointTimerActive.current = true;
        setVisible(isAdditionalSettings ? VALUES.TODAY_ACTIVE : VALUES.TODAY);
    };

    const onTimerStop = () => {
        isBreakPointTimerActive.current = false;
        setVisible(value => {
            if (isAdditionalSettings && value === VALUES.TODAY) {
                return VALUES.TOMORROW_ACTIVE;
            }
            if (value === VALUES.TOMORROW_ACTIVE || value === VALUES.TODAY_ACTIVE) return value;

            return VALUES.TOMORROW;
        });
    };

    const onCountDownTimerStart = () => setVisible(VALUES.TODAY_ACTIVE);
    const onCountDownTimerStop = () =>
        setVisible(isBreakPointTimerActive.current ? VALUES.TODAY_EXPIRED : VALUES.TOMORROW);

    return (
        <Switch keepMount visible={visible}>
            <Case className={cn(styles.external, { [styles.inline]: isToday })} id={VALUES.TODAY}>
                <TimerTextDisplay
                    endText={endTodayText}
                    startText={startTodayText}
                    styles={styles}
                    Text={Text}
                    timerText={timerText}
                >
                    <Timer breakPoint={breakPoint} onStart={onTimerStart} onStop={onTimerStop} />
                </TimerTextDisplay>
            </Case>
            <Case className={cn(styles.external)} id={VALUES.TODAY_ACTIVE} keepMount={false}>
                <TimerTextDisplay
                    endText={endTodayText}
                    startText={startTodayText}
                    styles={styles}
                    Text={Text}
                    timerText={timerText}
                >
                    <CountdownTimer
                        onStart={onCountDownTimerStart}
                        onStop={onCountDownTimerStop}
                        time={timerCount}
                    />
                </TimerTextDisplay>
            </Case>
            <Case className={cn(styles.external)} id={VALUES.TODAY_EXPIRED} keepMount={false}>
                {todayTextTimerExpired}
            </Case>
            <Case className={cn(styles.external)} id={VALUES.TOMORROW_ACTIVE} keepMount={false}>
                <TimerTextDisplay
                    endText={endTomorrowActiveText}
                    startText={startTomorrowActiveText}
                    styles={styles}
                    Text={Text}
                    timerText={timerText}
                >
                    <CountdownTimer
                        onStart={() => setVisible(VALUES.TOMORROW_ACTIVE)}
                        onStop={() => setVisible(VALUES.TOMORROW)}
                        time={timerCount}
                    />
                </TimerTextDisplay>
            </Case>
            <Case
                className={cn(styles.external, { [styles.inline]: !isToday })}
                id={VALUES.TOMORROW}
            >
                {tomorrowText}
            </Case>
        </Switch>
    );
};
